import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { getSizeWithinBounds } from '@folklore/size';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useRef, useCallback } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { getOptimalImageUrl } from '@micromag/core/utils';

var styles = {"container":"micromag-element-image-container","img":"micromag-element-image-img"};

var propTypes = {
  media: PropTypes$1.imageMedia,
  alt: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  resolution: PropTypes.number,
  objectFit: PropTypes$1.objectFit,
  containerStyle: PropTypes$1.containerStyle,
  imageStyle: PropTypes$1.containerStyle,
  className: PropTypes.string,
  imageClassName: PropTypes.string,
  onLoaded: PropTypes.func,
  loadingMode: PropTypes.string,
  shouldLoad: PropTypes.bool
};
var defaultProps = {
  media: null,
  alt: null,
  width: null,
  height: null,
  resolution: 1,
  objectFit: null,
  containerStyle: {},
  //
  imageStyle: {},
  className: null,
  imageClassName: null,
  onLoaded: null,
  loadingMode: 'lazy',
  shouldLoad: true
};
var Image = function Image(_ref) {
  var media = _ref.media,
    alt = _ref.alt,
    width = _ref.width,
    height = _ref.height,
    resolution = _ref.resolution,
    objectFit = _ref.objectFit,
    containerStyle = _ref.containerStyle,
    imageStyle = _ref.imageStyle,
    className = _ref.className,
    imageClassName = _ref.imageClassName,
    onLoaded = _ref.onLoaded,
    loadingMode = _ref.loadingMode,
    shouldLoad = _ref.shouldLoad;
  var _ref2 = media || {},
    _ref2$metadata = _ref2.metadata,
    metadata = _ref2$metadata === void 0 ? null : _ref2$metadata;
  var _ref3 = metadata || {},
    _ref3$width = _ref3.width,
    mediaWidth = _ref3$width === void 0 ? 0 : _ref3$width,
    _ref3$height = _ref3.height,
    mediaHeight = _ref3$height === void 0 ? 0 : _ref3$height,
    _ref3$description = _ref3.description,
    description = _ref3$description === void 0 ? 'image' : _ref3$description;
  var mediaRatio = mediaWidth / mediaHeight;
  var _useState = useState({
      width: mediaWidth,
      height: mediaHeight
    }),
    _useState2 = _slicedToArray(_useState, 2),
    _useState2$ = _useState2[0],
    _useState2$$width = _useState2$.width,
    realWidth = _useState2$$width === void 0 ? 0 : _useState2$$width,
    _useState2$$height = _useState2$.height,
    realHeight = _useState2$$height === void 0 ? 0 : _useState2$$height,
    setRealSize = _useState2[1];
  var wasLoadedRef = useRef(shouldLoad);
  if (shouldLoad && !wasLoadedRef.current) {
    wasLoadedRef.current = shouldLoad;
  }
  var finalShouldLoad = wasLoadedRef.current;
  var onImageLoaded = useCallback(function (e) {
    var _e$target = e.target,
      _e$target$naturalWidt = _e$target.naturalWidth,
      naturalWidth = _e$target$naturalWidt === void 0 ? 0 : _e$target$naturalWidt,
      _e$target$naturalHeig = _e$target.naturalHeight,
      naturalHeight = _e$target$naturalHeig === void 0 ? 0 : _e$target$naturalHeig;
    if (naturalWidth !== realWidth || naturalHeight !== realHeight) {
      setRealSize({
        width: naturalWidth || 0,
        height: naturalHeight || 0
      });
    }
    if (onLoaded !== null) {
      onLoaded(e);
    }
  }, [onLoaded]);
  var withFit = objectFit !== null;
  var mediaHasSize = realWidth > 0 && realHeight > 0;
  var finalContainerStyle;
  var finalImageStyle;
  if (withFit) {
    var imageTop = 0;
    var imageLeft = 0;
    var imageWidth = width;
    var imageHeight = height;
    var imageObjectFit = null;
    var imageObjectPosition = null;
    var _ref4 = objectFit || {},
      _ref4$fit = _ref4.fit,
      fit = _ref4$fit === void 0 ? null : _ref4$fit,
      _ref4$horizontalPosit = _ref4.horizontalPosition,
      horizontalPosition = _ref4$horizontalPosit === void 0 ? 'center' : _ref4$horizontalPosit,
      _ref4$verticalPositio = _ref4.verticalPosition,
      verticalPosition = _ref4$verticalPositio === void 0 ? 'center' : _ref4$verticalPositio;
    if (mediaHasSize) {
      var _getSizeWithinBounds = getSizeWithinBounds(realWidth, realHeight, width, height, {
          cover: fit === 'cover'
        }),
        resizedImageWidth = _getSizeWithinBounds.width,
        resizedImageHeight = _getSizeWithinBounds.height;
      imageWidth = resizedImageWidth;
      imageHeight = resizedImageHeight;
      if (horizontalPosition === 'center') {
        imageLeft = -(resizedImageWidth - width) / 2;
      } else if (horizontalPosition === 'right') {
        imageLeft = -(resizedImageWidth - width);
      }
      if (verticalPosition === 'center') {
        imageTop = -(resizedImageHeight - height) / 2;
      } else if (verticalPosition === 'bottom') {
        imageTop = -(resizedImageHeight - height);
      }
    } else {
      imageObjectFit = fit;
      imageObjectPosition = "".concat(horizontalPosition, " ").concat(verticalPosition);
    }
    finalContainerStyle = {
      width: width,
      height: height
    };
    finalImageStyle = {
      position: 'absolute',
      width: imageWidth,
      height: imageHeight,
      top: imageTop,
      left: imageLeft,
      objectFit: imageObjectFit,
      objectPosition: imageObjectPosition
    };
  } else {
    var validWidth = width !== null && typeof width === 'number';
    var validHeight = height !== null && typeof height === 'number';
    var ratioWidth = mediaRatio && validHeight ? height * mediaRatio : null;
    var ratioHeight = mediaRatio && validWidth ? width / mediaRatio : null;
    var _finalWidth = width !== null ? width : ratioWidth;
    var _finalHeight = height !== null ? height : ratioHeight;
    if (_finalWidth === null && _finalHeight === null) {
      _finalWidth = realWidth > 0 ? mediaWidth : null;
      _finalHeight = realHeight > 0 ? mediaHeight : null;
    }
    finalImageStyle = {
      width: _finalWidth,
      height: _finalHeight
    };
    finalContainerStyle = finalImageStyle;
  }
  finalContainerStyle = _objectSpread(_objectSpread({}, finalContainerStyle), containerStyle);
  finalImageStyle = _objectSpread(_objectSpread({}, finalImageStyle), imageStyle);
  var _finalImageStyle = finalImageStyle,
    _finalImageStyle$widt = _finalImageStyle.width,
    finalWidth = _finalImageStyle$widt === void 0 ? null : _finalImageStyle$widt,
    _finalImageStyle$heig = _finalImageStyle.height,
    finalHeight = _finalImageStyle$heig === void 0 ? null : _finalImageStyle$heig;
  var finalUrl = getOptimalImageUrl(media, finalWidth, finalHeight || finalWidth, {
    resolution: resolution
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, className, className !== null)]),
    style: finalContainerStyle
  }, finalUrl !== null && finalShouldLoad ? /*#__PURE__*/React.createElement("img", {
    src: finalUrl,
    alt: alt || description,
    className: classNames([styles.img, _defineProperty({}, imageClassName, imageClassName !== null)]),
    style: finalImageStyle,
    onLoad: onImageLoaded,
    loading: loadingMode
  }) : null);
};
Image.propTypes = propTypes;
Image.defaultProps = defaultProps;

export { Image as default };
